import { StyledSelect } from "@ando/ui";
import * as React from "react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2, Search } from "lucide-react";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  cn,
} from "@ando/ui";

/**
 * Shared DRY helpers for the org-detail tabs:
 *   - SortDir / cycleSort: tri-state column sort.
 *   - SortHeader: column header button with sort arrow + a11y.
 *   - SearchInput: debounced search input (300 ms) with aria-label.
 *   - usePagination: 25 / 50 / 100 page-size toggle.
 *   - ConfirmDialog: destructive confirm modal (focus trap is on
 *     Radix Dialog by default).
 *
 * Centralised so members/invites/audit/connectors don't reinvent the
 * wheel — and so a single bug-fix lands in 10 tabs at once.
 */

export type SortDir = "asc" | "desc" | null;

export function cycleSort(
  current: SortDir,
  active: boolean,
): { dir: SortDir; flip: boolean } {
  if (!active) return { dir: "asc", flip: true };
  if (current === "asc") return { dir: "desc", flip: false };
  if (current === "desc") return { dir: null, flip: false };
  return { dir: "asc", flip: false };
}

export interface SortHeaderProps {
  label: string;
  /** Stable column id used by the parent's sort state. */
  columnId: string;
  /** Currently sorted column id. */
  activeColumn: string | null;
  /** Current sort direction. */
  direction: SortDir;
  /** Click handler — receives next state. */
  onSort: (columnId: string, next: SortDir) => void;
  align?: "left" | "right";
  className?: string;
}

export function SortHeader({
  label,
  columnId,
  activeColumn,
  direction,
  onSort,
  align = "left",
  className,
}: SortHeaderProps): React.ReactElement {
  const active = activeColumn === columnId;
  const Icon = active
    ? direction === "asc"
      ? ArrowUp
      : direction === "desc"
        ? ArrowDown
        : ArrowUpDown
    : ArrowUpDown;
  return (
    <button
      type="button"
      onClick={() => {
        const { dir } = cycleSort(direction, active);
        onSort(columnId, active ? dir : "asc");
      }}
      aria-sort={
        !active
          ? "none"
          : direction === "asc"
            ? "ascending"
            : direction === "desc"
              ? "descending"
              : "none"
      }
      className={cn(
        "inline-flex items-center gap-1 font-semibold text-[10px] text-ink-3 uppercase tracking-wide transition-colors hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
        align === "right" && "ml-auto",
        className,
      )}
    >
      {label}
      <Icon
        size={11}
        aria-hidden
        className={active ? "text-accent" : "opacity-60"}
      />
    </button>
  );
}

export interface SearchInputProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  ariaLabel: string;
}

/** Debounced text input. Calls ``onChange`` 300 ms after typing stops. */
export function SearchInput({
  value,
  onChange,
  placeholder,
  ariaLabel,
}: SearchInputProps): React.ReactElement {
  const { i18n } = useLingui();
  const [local, setLocal] = React.useState(value);
  // Sync local FROM parent only when parent's ``value`` actually changes
  // AND it differs from what we already hold. The functional updater
  // reads the freshest ``local`` without subscribing the effect to it.
  // Without this guard the pair of effects could ping-pong on every
  // commit and trip React #185 ("Maximum update depth exceeded") when
  // ``onChange`` re-renders the parent which re-passes the same
  // ``value`` reference (caught May 23, 2026).
  React.useEffect(() => {
    setLocal((prev) => (prev === value ? prev : value));
  }, [value]);
  React.useEffect(() => {
    if (local === value) return;
    const id = setTimeout(() => {
      onChange(local);
    }, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local]);
  return (
    <label className="relative inline-flex max-w-sm flex-1 items-center">
      <Search
        size={13}
        aria-hidden
        className="-translate-y-1/2 absolute top-1/2 left-2.5 text-ink-3"
      />
      <input
        type="search"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        placeholder={placeholder ?? i18n._(msg`Cerca…`)}
        aria-label={ariaLabel}
        className="h-8 w-full rounded-md border border-hairline bg-card pr-3 pl-8 text-[12.5px] text-ink-1 transition-colors placeholder:text-ink-3 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
      />
    </label>
  );
}

export type PageSize = 25 | 50 | 100;

export function usePagination(initial: PageSize = 25) {
  const [pageSize, setPageSize] = React.useState<PageSize>(initial);
  const [page, setPage] = React.useState(1);
  React.useEffect(() => {
    setPage(1);
  }, [pageSize]);
  return { page, setPage, pageSize, setPageSize };
}

export interface PaginationControlsProps {
  page: number;
  pageSize: PageSize;
  total: number;
  onPageChange: (next: number) => void;
  onPageSizeChange: (next: PageSize) => void;
}

export function PaginationControls({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
}: PaginationControlsProps): React.ReactElement {
  const { i18n } = useLingui();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 pt-2 text-[12px] text-ink-3">
      <div>
        {total === 0
          ? i18n._(msg`0 risultati`)
          : i18n._(msg`${from}–${to} di ${total}`)}
      </div>
      <div className="flex items-center gap-2">
        <label className="inline-flex items-center gap-1.5">
          <span>{i18n._(msg`Per pagina`)}</span>
          <StyledSelect
            value={pageSize}
            onChange={(e) =>
              onPageSizeChange(Number(e.target.value) as PageSize)
            }
            aria-label={i18n._(msg`Righe per pagina`)}
            className="h-7 rounded border border-hairline bg-card px-1.5 text-[12px] text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </StyledSelect>
        </label>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            aria-label={i18n._(msg`Pagina precedente`)}
          >
            ‹
          </Button>
          <span className="tabular-nums">
            {page} / {totalPages}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            aria-label={i18n._(msg`Pagina successiva`)}
          >
            ›
          </Button>
        </div>
      </div>
    </div>
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: React.ReactNode;
  confirmLabel?: React.ReactNode;
  destructive?: boolean;
  loading?: boolean;
  onConfirm: () => void;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  destructive = true,
  loading = false,
  onConfirm,
}: ConfirmDialogProps): React.ReactElement {
  const { i18n } = useLingui();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            {i18n._(msg`Annulla`)}
          </Button>
          <Button
            type="button"
            variant={destructive ? "destructive" : "default"}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <Loader2 size={14} className="mr-2 animate-spin" aria-hidden />
            ) : null}
            {confirmLabel ?? i18n._(msg`Conferma`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export interface BeGapBannerProps {
  message: React.ReactNode;
}

/**
 * Yellow banner used when a BE endpoint required by a tab is missing.
 * Tabs that depend on absent endpoints render this in place of the
 * "real" UI so users see the gap without the tab crashing.
 */
export function BeGapBanner({
  message,
}: BeGapBannerProps): React.ReactElement {
  const { i18n } = useLingui();
  return (
    <div
      role="status"
      className="rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-[12.5px] text-ink-2"
    >
      <span className="font-semibold text-warning-foreground">
        {i18n._(msg`BE gap`)} ·{" "}
      </span>
      {message}
    </div>
  );
}

/** Small utility — generic loading row. */
export function LoadingRow({
  label,
}: {
  label?: string;
}): React.ReactElement {
  const { i18n } = useLingui();
  return (
    <div
      role="status"
      className="flex items-center gap-2 py-4 text-[12px] text-ink-3"
    >
      <Loader2 size={13} className="animate-spin" aria-hidden />{" "}
      {label ?? i18n._(msg`Caricamento…`)}
    </div>
  );
}


// ── Shared formatting helpers (consolidated June 2026 DRY pass) ──────


/**
 * Canonical mapping ``usage_multiplier → tier label`` used across the
 * org-detail surface (shell header badge, member settings dialog,
 * billing card, …). DRY: ALL tier-label lookups must go through
 * :func:`tierLabel` so adding a new tier is a single-line change here.
 *
 * Multipliers outside the known set fall back to ``"Custom"`` so legacy
 * rows (e.g. pre-launch ``×0`` admin orgs, hand-edited customer plans)
 * render gracefully without crashing the typed lookup.
 */
const TIER_LABEL_BY_MULTIPLIER: Readonly<Record<number, string>> = {
  1: "Standard",
  3: "Pro",
  5: "Business",
  10: "Business+",
  20: "Enterprise",
  50: "Enterprise+",
};


export function tierLabel(multiplier: number): string {
  return TIER_LABEL_BY_MULTIPLIER[multiplier] ?? "Custom";
}


/**
 * Safe ``Number`` coercion — returns ``fallback`` when the input isn't a
 * finite number. The BE declares numerous percentage / cost / storage
 * fields as required ``number`` on the wire but occasionally omits them
 * for placeholder / multiplier=0 orgs; without this guard
 * ``.toFixed`` / arithmetic crashes the detail route.
 *
 * Centralised so every tab uses the same lenient coercion and a single
 * fix in the future (e.g. logging the bad value) lands everywhere.
 */
export function safeNum(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
